import { logger } from './logger';

// Utility to suppress specific console errors and warnings
// and integrate with Sentry logging
export const setupErrorHandling = () => {
  const originalConsoleError = console.error;
  const originalConsoleWarn = console.warn;
  const originalConsoleLog = console.log;
  const originalConsoleInfo = console.info;

  // List of errors to suppress
  const suppressedErrors = [
    'ERR_BLOCKED_BY_CLIENT',
    'Permissions-Policy',
    'private-state-token',
    'browsing-topics',
    'ambient-light-sensor',
    'battery',
    'vr',
    'sentry.io',
    '6577-7f2a92f86947d649.js',
    'ingest.sentry.io',
    'ResizeObserver loop',
    'envelope',
    'sendEnvelope',
    '_flushOutcomes'
  ];

  // Convert console.log to use our structured logger
  console.log = (...args) => {
    // Use our logger for all console.log calls
    if (args.length > 0) {
      const message = typeof args[0] === 'string' ? args[0] : 'Log';
      const data = args.length > 1 ? args.slice(1) : undefined;
      logger.debug(message, data);
    }
    // Still call original to maintain backward compatibility
    originalConsoleLog.apply(console, args);
  };
  
  // Convert console.info to use our structured logger
  console.info = (...args) => {
    if (args.length > 0) {
      const message = typeof args[0] === 'string' ? args[0] : 'Info';
      const data = args.length > 1 ? args.slice(1) : undefined;
      logger.info(message, data);
    }
    originalConsoleInfo.apply(console, args);
  };

  // Override console.error
  console.error = (...args) => {
    if (args.length > 0) {
      const firstArg = args[0];
      
      if (typeof firstArg === 'string') {
        const shouldSuppress = suppressedErrors.some(error => 
          firstArg.includes(error)
        );
        if (shouldSuppress) {
          return;
        }
        
        // Send to our logger
        const error = args.length > 1 && args[1] instanceof Error ? args[1] : new Error(firstArg);
        const context = args.length > 2 ? args.slice(2) : undefined;
        logger.error(firstArg, error, context);
      } else if (firstArg instanceof Error) {
        // Handle case where an Error object is passed directly
        logger.error(firstArg.message, firstArg);
      }
    }
    originalConsoleError.apply(console, args);
  };

  // Override console.warn
  console.warn = (...args) => {
    if (args.length > 0) {
      const firstArg = args[0];
      
      if (typeof firstArg === 'string') {
        const shouldSuppress = suppressedErrors.some(error => 
          firstArg.includes(error)
        );
        if (shouldSuppress) return;
        
        // Send to our logger
        const data = args.length > 1 ? args.slice(1) : undefined;
        logger.warn(firstArg, data);
      }
    }
    originalConsoleWarn.apply(console, args);
  };
  
  // Set up global error handlers
  window.addEventListener('error', (event) => {
    logger.error('Unhandled error', event.error || new Error(event.message));
  });
  
  window.addEventListener('unhandledrejection', (event) => {
    logger.error('Unhandled promise rejection', event.reason || new Error('Promise rejected'));
  });
  
  logger.info('Error handling setup complete');
};